import React from 'react';

import { PageLayout } from '@/components/layout/PageLayout';
import { FeaturedHero } from '@/components/post/FeaturedHero';
import {
  CATEGORY_BLOCK_SIZE,
  HomeSection,
  LATEST_BLOCK_SIZE,
  categoryBlockLayout,
} from '@/components/post/HomeSections';
import { AdBanner } from '@/components/site/AdBanner';
import { SEO } from '@/components/ui/SEO';
import { useFeaturedPostQuery, usePostsInfiniteQuery } from '@/hooks/usePostsQueries';
import { CATEGORIES, type CategoryConfig, type CategorySlug } from '@/lib/categories';

/** Order of the category blocks on the home page (the menu and footer keep CATEGORIES' order) */
const HOME_CATEGORY_ORDER: CategorySlug[] = ['hardtechno', 'hardstyle', 'culture'];
const HOME_CATEGORIES = HOME_CATEGORY_ORDER.map(
  (slug) => CATEGORIES.find((category) => category.slug === slug)!
);

/** One category's block; skips the articles already shown higher up on the page. */
const CategoryHomeSection: React.FC<{
  category: CategoryConfig;
  index: number;
  excludeIds: Set<string>;
}> = ({ category, index, excludeIds }) => {
  // Same cache as the category page, so opening it after the home page is instant
  const { posts, isPending, error } = usePostsInfiniteQuery({ section: category.slug });
  const blockPosts = posts.filter((post) => !excludeIds.has(post.id)).slice(0, CATEGORY_BLOCK_SIZE);

  return (
    <HomeSection
      title={category.title}
      subtitle={category.subtitle}
      moreLink={{ to: category.path, label: 'Tovább a rovatra' }}
      posts={blockPosts}
      isPending={isPending}
      error={error}
      {...categoryBlockLayout(index)}
    />
  );
};

export const HomePage: React.FC = () => {
  const featured = useFeaturedPostQuery();
  const latest = usePostsInfiniteQuery();

  // Every article appears once on the page: the hero first, then the newest, then the categories
  const shownIds = new Set<string>(featured.data ? [featured.data.id] : []);
  const latestPosts = latest.posts
    .filter((post) => !shownIds.has(post.id))
    .slice(0, LATEST_BLOCK_SIZE);
  latestPosts.forEach((post) => shownIds.add(post.id));

  return (
    <PageLayout>
      <SEO
        title="Főoldal"
        description="Magyarország legfrissebb hardstyle és hard techno híroldala. Hírek, ajánlók, beszámolók és kulturális cikkek a hard zenei színtérről."
        url="https://hardnewshungary.com"
        type="website"
      />
      <FeaturedHero />
      <AdBanner />

      <HomeSection
        title="Legfrissebb cikkek"
        moreLink={{ to: '/blog', label: 'Összes cikk' }}
        posts={latestPosts}
        isPending={latest.isPending || featured.isPending}
        error={latest.error}
        layout="feature"
        leadSize="lg"
      />

      {HOME_CATEGORIES.map((category, index) => (
        <CategoryHomeSection
          key={category.slug}
          category={category}
          index={index}
          excludeIds={shownIds}
        />
      ))}
    </PageLayout>
  );
};
